import { useCallback, useEffect, useRef, useState } from "react";

import {
  abandonGameSession,
  fetchCollection,
  fetchCollections,
  fetchDashboard,
  fetchFolder,
  fetchFolders,
  fetchFavorites,
  fetchMemoSession,
  fetchSession,
  markFolderRead,
  startDuelSession,
  startMemo,
  startQuickPlay,
  submitMemoAttempt,
  toggleFolderSubscription,
  toggleFolderPublication,
  toggleFolderOfficial,
  toggleCollectionOfficial,
  toggleCollectionFavorite,
} from "./api";
import { destinationForPath, navigate } from "./navigation";
import { DuelsPage } from "./Duels";
import { CatalogImportPage } from "./CatalogImport";
import { OtherGame } from "./OtherGames";
import { RevisioGame, ReviewLibrary } from "./Revisio";
import { HistoryPage } from "./History";
import { RecommendationsPage } from "./Recommendations";
import { ClassroomsPage } from "./Classrooms";
import { FolderEditor } from "./FolderEditor";
import { CollectionEditor } from "./CollectionEditor";
import { ProfilePage } from "./Profile";
import { publishFavoriteChange, subscribeFavoriteChanges } from "./favorites";
import { publishScoreChange, subscribeScoreBroadcasts } from "./score";
import {
  AbandonActivityButton,
  AbandonedActivityPanel,
  ActivityGuide,
  DailyGoalCard,
  defaultGuide,
  FeedbackNotice,
  RewardSummary,
  SessionProgressBar,
} from "./Experience";
import type {
  DashboardResponse,
  CardFace,
  CollectionCard,
  CollectionDetail,
  CollectionSummary,
  FolderDetail,
  FolderSummary,
  ExperienceFeedback,
  ExperienceProgress,
  ExperienceReward,
  FavoritesResponse,
  MemoDifficulty,
  MemoFace,
  MemoResult,
  MemoSession,
  PedagogyDestination,
  PracticeGameSlug,
  SessionResponse,
  SessionUser,
  UserScoreSnapshot,
} from "./types";

const icons: Record<string, string> = {
  review: "↻",
  spark: "✦",
  star: "★",
  history: "◷",
  cards: "▤",
  chain: "⌁",
  classroom: "♟",
  book: "▱",
  duel: "⚡",
};

function csrfToken(): string {
  const cookie = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith("csrftoken="));
  return cookie ? decodeURIComponent(cookie.slice("csrftoken=".length)) : "";
}

function Brand() {
  return (
    <a
      className="brand"
      href="/"
      onClick={(event) => {
        event.preventDefault();
        navigate("/");
      }}
      aria-label="Accueil Cogito"
    >
      <span className="brand-mark" aria-hidden="true">
        C
      </span>
      <span>Cogito</span>
    </a>
  );
}

function LoadingScreen() {
  return (
    <main className="centered-state" aria-live="polite">
      <span className="loader" aria-hidden="true" />
      <p>Préparation de votre espace pédagogique…</p>
    </main>
  );
}

function QuickPlayButton({ compact = false }: { compact?: boolean }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const quickGame = await startQuickPlay();
      navigate(quickGame.path);
    } catch (reason) {
      setError(
        reason instanceof Error && reason.message.includes("no_quick_game_available")
          ? "Aucune partie adaptée n’est disponible pour le moment."
          : reason instanceof Error
            ? reason.message
            : "La partie rapide est indisponible.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={compact ? "quick-play compact" : "quick-play"}>
      <button className="button button-primary" type="button" disabled={loading} onClick={() => void start()}>
        {loading ? "Choix de la partie…" : "Partie rapide"}
      </button>
      {error && (
        <small role="alert">
          {error}{" "}
          <a href="/ressources-officielles">Explorer les ressources</a>
          {" · "}
          <a href="/mes-revisions">Voir mes révisions</a>
        </small>
      )}
    </div>
  );
}

function FavoriteButton({
  collectionId,
  isFavorite,
  onChanged,
}: {
  collectionId: number;
  isFavorite: boolean;
  onChanged: (favorite: boolean) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFavorite, setCurrentFavorite] = useState(isFavorite);

  useEffect(() => setCurrentFavorite(isFavorite), [isFavorite]);
  useEffect(
    () => subscribeFavoriteChanges((change) => {
      if (change.collectionId === collectionId) setCurrentFavorite(change.isFavorite);
    }),
    [collectionId],
  );

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await toggleCollectionFavorite(collectionId, !currentFavorite);
      setCurrentFavorite(result.is_favorite);
      onChanged(result.is_favorite);
      publishFavoriteChange({ collectionId, isFavorite: result.is_favorite });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Le favori n’a pas pu être modifié.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <span className="favorite-action">
      <button
        className={currentFavorite ? "favorite-button active" : "favorite-button"}
        type="button"
        disabled={saving}
        aria-pressed={currentFavorite}
        aria-label={currentFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
        onClick={() => void toggle()}
      >
        {currentFavorite ? "★" : "☆"}
      </button>
      {error && <small role="alert">{error}</small>}
    </span>
  );
}

function AnonymousLanding({ session }: { session: SessionResponse }) {
  const requestedPath = window.location.pathname;

  useEffect(() => {
    if (requestedPath !== "/") {
      window.location.replace(
        `/accounts/login/?next=${encodeURIComponent(requestedPath)}`,
      );
    }
  }, [requestedPath]);

  if (requestedPath !== "/") return <LoadingScreen />;

  return (
    <main className="landing-shell">
      <nav className="landing-nav" aria-label="Navigation principale">
        <Brand />
        <a className="button button-secondary" href={session.auth.login_url}>
          Se connecter
        </a>
      </nav>
      <section className="landing-hero">
        <div className="landing-copy">
          <p className="eyebrow">Apprendre à son rythme</p>
          <h1>Comprendre, mémoriser, progresser.</h1>
          <p className="lead">
            Cogito réunit séries de cartes, révisions et parcours de classe dans un espace
            pédagogique pensé pour les élèves et les enseignants.
          </p>
          <div className="landing-actions">
            <a className="button button-primary" href={session.auth.login_url}>
              Ouvrir mon espace
            </a>
            {session.auth.registration_url && (
              <a className="landing-register" href={session.auth.registration_url}>Créer un compte</a>
            )}
            <span className="auth-mode">Accès sécurisé à votre espace</span>
          </div>
        </div>
        <div className="learning-orbit" aria-label="Cycle pédagogique Cogito">
          <div className="orbit-center">
            <span aria-hidden="true">C</span>
            <strong>Cogito</strong>
          </div>
          <span className="orbit-step orbit-step-one">Découvrir</span>
          <span className="orbit-step orbit-step-two">Pratiquer</span>
          <span className="orbit-step orbit-step-three">Maîtriser</span>
        </div>
      </section>
      <section className="principles" aria-label="Principes pédagogiques">
        <article>
          <span>01</span>
          <h2>Des contenus structurés</h2>
          <p>Vos séries et chaînes organisent clairement chaque parcours.</p>
        </article>
        <article>
          <span>02</span>
          <h2>Une pratique active</h2>
          <p>Les jeux et révisions accompagnent une progression régulière.</p>
        </article>
        <article>
          <span>03</span>
          <h2>Un suivi lisible</h2>
          <p>Élèves et enseignants suivent leurs activités et leurs progrès.</p>
        </article>
      </section>
    </main>
  );
}

function DestinationCard({ destination }: { destination: PedagogyDestination }) {
  return (
    <a
      className="destination-card"
      href={destination.path}
      onClick={(event) => {
        event.preventDefault();
        navigate(destination.path);
      }}
    >
      <span className={`destination-icon icon-${destination.icon}`} aria-hidden="true">
        {icons[destination.icon] ?? "•"}
      </span>
      <span className="destination-copy">
        <strong>{destination.label}</strong>
        <span>{destination.description}</span>
      </span>
      <span className="destination-arrow" aria-hidden="true">
        →
      </span>
    </a>
  );
}

function FeatureUnavailable({ destination }: { destination: PedagogyDestination }) {
  return (
    <section className="feature-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>
        ← Retour au tableau de bord
      </button>
      <div className="feature-panel">
        <span className={`feature-icon icon-${destination.icon}`} aria-hidden="true">
          {icons[destination.icon] ?? "•"}
        </span>
        <p className="eyebrow">Prochainement dans Cogito</p>
        <h1>{destination.label}</h1>
        <p className="lead">{destination.description}</p>
        <div className="info-notice" role="status">
          <strong>Cette fonctionnalité est en préparation</strong>
          <p>Elle rejoindra progressivement votre espace pédagogique.</p>
        </div>
        <button className="button button-primary" type="button" onClick={() => navigate("/")}>
          Revenir à mes activités
        </button>
      </div>
    </section>
  );
}

function FaceContent({ face }: { face: CardFace | MemoFace | undefined }) {
  if (!face) return <p className="face-missing">Cette face n’est pas encore renseignée.</p>;

  return (
    <div className="face-content">
      {face.text && <p className="face-text">{face.text}</p>}
      {face.image && <img src={face.image} alt={face.image_alt_text ?? ""} />}
      {face.audio && (
        <div className="face-audio">
          {face.audio_alt_text && <p>{face.audio_alt_text}</p>}
          <audio controls preload="none" src={face.audio} />
        </div>
      )}
      {face.latex_text && <code className="latex-text">{face.latex_text}</code>}
      {face.latex_image && (
        <img src={face.latex_image} alt={face.latex_image_alt_text ?? "Formule mathématique"} />
      )}
      {face.description && <p className="face-description">{face.description}</p>}
    </div>
  );
}

function cardFaces(card: CollectionCard): { front: CardFace | undefined; backs: CardFace[] } {
  return {
    front: card.faces.find((face) => face.type === "front"),
    backs: card.faces.filter((face) => face.type !== "front"),
  };
}

function OfficialFolderCatalog() {
  const [folders, setFolders] = useState<FolderSummary[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void fetchFolders(false, 1, 24, true)
      .then((payload) => {
        if (!active) return;
        setFolders(payload.folders);
        setHasNext(payload.pagination.has_next);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Les chaînes officielles sont indisponibles.");
      });
    return () => { active = false; };
  }, []);

  const loadMore = async () => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const payload = await fetchFolders(false, nextPage, 24, true);
      setFolders((current) => [...(current ?? []), ...payload.folders]);
      setPage(nextPage);
      setHasNext(payload.pagination.has_next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Page suivante indisponible.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="official-folder-catalog" aria-labelledby="official-folders-title">
      <div className="section-heading"><div><p className="eyebrow">Parcours validés</p><h2 id="official-folders-title">Chaînes officielles</h2><p>Des parcours ordonnés regroupant plusieurs séries officielles.</p></div></div>
      {error && <div className="info-notice" role="alert">{error}</div>}
      {folders === null && !error ? <div className="collection-loading">Chargement des chaînes…</div> : folders?.length ? <>
        <div className="collection-grid">{folders.map((folder) => <article className="collection-tile" key={folder.id}>
          <div className="collection-tile-top"><span className="subject-color" style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(folder.subject.color) ? folder.subject.color : "#5f63bb" }} aria-hidden="true" /><span>{folder.subject.name}</span><strong>Officielle</strong></div>
          <h3>{folder.title}</h3><p>{folder.description ?? "Aucune description."}</p>
          <div className="collection-meta"><span>{folder.collections_count} série{folder.collections_count > 1 ? "s" : ""}</span></div>
          <button className="button button-primary" type="button" onClick={() => navigate(folder.path)}>Ouvrir la chaîne</button>
        </article>)}</div>
        {hasNext && <button className="button" type="button" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? "Chargement…" : "Afficher plus de chaînes"}</button>}
      </> : <div className="empty-state collection-empty"><strong>Aucune chaîne officielle disponible</strong><p>Les parcours validés apparaîtront ici après leur publication.</p></div>}
    </section>
  );
}

function CollectionLibrary({ official, canImport }: { official: boolean; canImport: boolean }) {
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setCollections(null);
    setPage(1);
    setHasNext(false);
    setError(null);
    void fetchCollections(official, 1)
      .then((payload) => {
        if (active) {
          setCollections(payload.collections);
          setHasNext(payload.pagination.has_next);
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : "Les séries sont indisponibles.");
        }
      });
    return () => {
      active = false;
    };
  }, [official]);

  const loadMore = async () => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const payload = await fetchCollections(official, nextPage);
      setCollections((current) => [...(current ?? []), ...payload.collections]);
      setPage(nextPage);
      setHasNext(payload.pagination.has_next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Page suivante indisponible.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="collection-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>
        ← Retour au tableau de bord
      </button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">Contenus pédagogiques</p>
          <h1>{official ? "Ressources officielles" : "Mes séries"}</h1>
          <p>
            {official
              ? "Les séries et chaînes publiées, validées pour tous les profils."
              : "Vos séries et les contenus publiés accessibles depuis Cogito."}
          </p>
        </div>
        <div className="collection-heading-actions">
          {!official && canImport && <><button className="button button-primary" type="button" onClick={() => navigate("/mes-series/nouvelle")}>Créer une série</button><button className="button" type="button" onClick={() => navigate("/mes-series/importer")}>Importer un catalogue</button></>}
          <span className="section-chip">Catalogue Cogito</span>
        </div>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {collections === null && !error ? (
        <div className="collection-loading" aria-live="polite">Chargement des séries…</div>
      ) : collections?.length ? (
        <>
          <div className="collection-grid">
            {collections.map((collection) => (
            <article className="collection-tile" key={collection.id}>
              <div className="collection-tile-top">
                <span
                  className="subject-color"
                  style={{
                    backgroundColor: /^#[0-9a-f]{6}$/i.test(collection.subject.color)
                      ? collection.subject.color
                      : "#5f63bb",
                  }}
                  aria-hidden="true"
                />
                <span>{collection.subject.name}</span>
                {collection.is_official && <strong>Officielle</strong>}
              </div>
              <h2>{collection.title}</h2>
              <FavoriteButton
                collectionId={collection.id}
                isFavorite={Boolean(collection.is_favorite)}
                onChanged={(favorite) => setCollections((current) => current?.map((item) => item.id === collection.id ? { ...item, is_favorite: favorite } : item) ?? null)}
              />
              <p>{collection.description ?? "Aucune description."}</p>
              <div className="collection-meta">
                <span>{collection.cards_count} carte{collection.cards_count > 1 ? "s" : ""}</span>
                {!collection.is_published && <span>Brouillon</span>}
              </div>
              <button className="button button-primary" type="button" onClick={() => navigate(collection.path)}>
                Ouvrir la série
              </button>
            </article>
            ))}
          </div>
          {hasNext && (
            <button className="button" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "Chargement…" : "Afficher plus de séries"}
            </button>
          )}
        </>
      ) : (
        <div className="empty-state collection-empty">
          <strong>Aucune série disponible</strong>
          <p>Les contenus publiés ou créés par votre profil apparaîtront ici.</p>
        </div>
      )}
      {official && <OfficialFolderCatalog />}
    </section>
  );
}

function CollectionStudy({ collectionId }: { collectionId: number }) {
  const [collection, setCollection] = useState<CollectionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"list" | "flip">("list");
  const [cardIndex, setCardIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [officialSaving, setOfficialSaving] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchCollection(collectionId)
      .then((payload) => {
        if (active) setCollection(payload);
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : "Cette série est indisponible.");
        }
      });
    return () => {
      active = false;
    };
  }, [collectionId]);

  const toggleOfficial = async () => {
    if (!collection || officialSaving) return;
    setOfficialSaving(true);
    setError(null);
    try {
      const result = await toggleCollectionOfficial(collection.id);
      setCollection({ ...collection, is_official: result.is_official, is_published: result.is_published });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Le statut officiel n’a pas pu être modifié.");
    } finally {
      setOfficialSaving(false);
    }
  };

  if (error) {
    return (
      <section className="feature-page">
        <button className="back-button" type="button" onClick={() => navigate("/mes-series")}>
          ← Retour aux séries
        </button>
        <div className="info-notice">{error}</div>
      </section>
    );
  }
  if (!collection) return <div className="collection-loading">Chargement de la série…</div>;

  const activeCard = collection.cards[cardIndex];
  const activeFaces = activeCard ? cardFaces(activeCard) : null;
  const backLabel = activeFaces?.backs.length === 1 ? "Verso" : "Réponses";

  return (
    <section className="study-page">
      <button className="back-button" type="button" onClick={() => navigate("/mes-series")}>
        ← Retour aux séries
      </button>
      <header className="study-heading">
        <div>
          <p className="eyebrow">{collection.subject.name}</p>
          <h1>{collection.title}</h1>
          <p>{collection.description}</p>
        </div>
        <div className="study-actions">
          <FavoriteButton
            collectionId={collection.id}
            isFavorite={collection.is_favorite}
            onChanged={(favorite) => setCollection({ ...collection, is_favorite: favorite })}
          />
          {collection.can_validate_official && (
            <button className="button" type="button" disabled={officialSaving} onClick={() => void toggleOfficial()}>
              {collection.is_official ? "Retirer le statut officiel" : "Valider comme ressource officielle"}
            </button>
          )}
          {collection.modes.asso && (
            <button className="button" type="button" onClick={() => navigate(`/series/${collection.id}/association`)}>
              Jouer à Asso
            </button>
          )}
          {collection.modes.memo && (
            <button
              className="button button-primary"
              type="button"
              onClick={() => navigate(`/series/${collection.id}/memo`)}
            >
              Jouer à Memo
            </button>
          )}
          {collection.modes.brio && (
            <button className="button" type="button" onClick={() => navigate(`/series/${collection.id}/brio`)}>
              Jouer à Brio
            </button>
          )}
          {collection.modes.typo && (
            <button className="button" type="button" onClick={() => navigate(`/series/${collection.id}/typographie`)}>
              Jouer à Typo
            </button>
          )}
          {collection.modes.quizako && (
            <button className="button" type="button" onClick={() => navigate(`/series/${collection.id}/quizako`)}>
              Jouer à Quizako
            </button>
          )}
          <button className="button" type="button" onClick={() => navigate(`/series/${collection.id}/revisio`)}>
            Réviser avec Revisio
          </button>
          <div className="mode-switch" aria-label="Mode d’affichage">
            <button className={mode === "list" ? "active" : ""} type="button" onClick={() => setMode("list")}>
              Liste
            </button>
            <button className={mode === "flip" ? "active" : ""} type="button" onClick={() => setMode("flip")}>
              Recto‑Verso
            </button>
          </div>
        </div>
      </header>

      {collection.cards.length === 0 ? (
        <div className="empty-state collection-empty">Cette série ne contient encore aucune carte.</div>
      ) : mode === "list" ? (
        <ol className="card-list">
          {collection.cards.map((card) => {
            const faces = cardFaces(card);
            return (
              <li key={card.id}>
                <article>
                  <span className="card-number">{card.position + 1}</span>
                  <div>
                    <p className="card-side-label">Recto</p>
                    <FaceContent face={faces.front} />
                  </div>
                  <div>
                    <p className="card-side-label">{faces.backs.length === 1 ? "Verso" : "Réponses"}</p>
                    {faces.backs.map((face) => (
                      <FaceContent key={face.id} face={face} />
                    ))}
                  </div>
                </article>
              </li>
            );
          })}
        </ol>
      ) : activeCard && activeFaces ? (
        <div className="flip-study">
          <p className="card-progress">Carte {cardIndex + 1} sur {collection.cards.length}</p>
          <button
            className={`flip-card ${showBack ? "is-back" : ""}`}
            type="button"
            onClick={() => setShowBack((value) => !value)}
            aria-label={showBack ? "Afficher le recto" : `Afficher ${backLabel.toLowerCase()}`}
          >
            <span className="card-side-label">{showBack ? backLabel : "Recto"}</span>
            {showBack ? (
              activeFaces.backs.map((face) => <FaceContent key={face.id} face={face} />)
            ) : (
              <FaceContent face={activeFaces.front} />
            )}
            <small>Cliquer pour retourner la carte</small>
          </button>
          <div className="study-controls">
            <button
              type="button"
              disabled={cardIndex === 0}
              onClick={() => {
                setCardIndex((value) => value - 1);
                setShowBack(false);
              }}
            >
              ← Précédente
            </button>
            <button
              type="button"
              disabled={cardIndex === collection.cards.length - 1}
              onClick={() => {
                setCardIndex((value) => value + 1);
                setShowBack(false);
              }}
            >
              Suivante →
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function FolderLibrary({ canOfficial }: { canOfficial: boolean }) {
  const [folders, setFolders] = useState<FolderSummary[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void fetchFolders(!canOfficial, 1)
      .then((payload) => {
        if (active) {
          setFolders(payload.folders);
          setHasNext(payload.pagination.has_next);
        }
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Les chaînes sont indisponibles.");
      });
    return () => {
      active = false;
    };
  }, [canOfficial]);

  const loadMore = async () => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const payload = await fetchFolders(!canOfficial, nextPage);
      setFolders((current) => [...(current ?? []), ...payload.folders]);
      setPage(nextPage);
      setHasNext(payload.pagination.has_next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Page suivante indisponible.");
    } finally {
      setLoadingMore(false);
    }
  };

  const togglePublication = async (folder: FolderSummary) => {
    setError(null);
    try {
      const result = await toggleFolderPublication(folder.id);
      setFolders((current) => current?.map((item) => item.id === folder.id ? { ...item, is_published: result.is_published } : item) ?? null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La publication n’a pas pu être modifiée.");
    }
  };

  const toggleOfficial = async (folder: FolderSummary) => {
    setError(null);
    try {
      const result = await toggleFolderOfficial(folder.id);
      setFolders((current) => current?.map((item) => item.id === folder.id ? { ...item, is_official: result.is_official, is_published: result.is_published } : item) ?? null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Le statut officiel n’a pas pu être modifié.");
    }
  };

  return (
    <section className="collection-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>
        ← Retour au tableau de bord
      </button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">Parcours pédagogiques</p>
          <h1>Mes chaînes</h1>
          <p>Regroupez vos séries dans des parcours cohérents et publiables.</p>
        </div>
        <div className="collection-heading-actions">
          <button className="button button-primary" type="button" onClick={() => navigate("/mes-chaines/nouvelle")}>
            Créer une chaîne
          </button>
          <span className="section-chip">Chaînes Cogito</span>
        </div>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {folders === null && !error ? (
        <div className="collection-loading" aria-live="polite">Chargement des chaînes…</div>
      ) : folders?.length ? (
        <>
          <div className="collection-grid">
            {folders.map((folder) => (
              <article className="collection-tile" key={folder.id}>
                <div className="collection-tile-top">
                  <span
                    className="subject-color"
                    style={{
                      backgroundColor: /^#[0-9a-f]{6}$/i.test(folder.subject.color)
                        ? folder.subject.color
                        : "#5f63bb",
                    }}
                    aria-hidden="true"
                  />
                  <span>{folder.subject.name}</span>
                  {folder.is_official && <strong>Officielle</strong>}
                </div>
                <h2>{folder.title}</h2>
                <p>{folder.description ?? "Aucune description."}</p>
                <div className="collection-meta">
                  <span>{folder.collections_count} série{folder.collections_count > 1 ? "s" : ""}</span>
                  {!folder.is_published && <span>Brouillon</span>}
                </div>
                <button className="button button-primary" type="button" onClick={() => navigate(folder.path)}>
                  Ouvrir la chaîne
                </button>
                {folder.can_publish && (
                  <button className="button" type="button" onClick={() => void togglePublication(folder)}>
                    {folder.is_published ? "Repasser en brouillon" : "Publier la chaîne"}
                  </button>
                )}
                {folder.can_validate_official && (
                  <button className="button" type="button" onClick={() => void toggleOfficial(folder)}>
                    {folder.is_official ? "Retirer le statut officiel" : "Valider comme officielle"}
                  </button>
                )}
              </article>
            ))}
          </div>
          {hasNext && (
            <button className="button" type="button" disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? "Chargement…" : "Afficher plus de chaînes"}
            </button>
          )}
        </>
      ) : (
        <div className="empty-state collection-empty">
          <strong>Aucune chaîne créée</strong>
          <p>Les chaînes créées dans l’administration Cogito apparaîtront ici.</p>
        </div>
      )}
    </section>
  );
}

function FolderStudy({ folderId }: { folderId: number }) {
  const [folder, setFolder] = useState<FolderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchFolder(folderId)
      .then((payload) => {
        if (!active) return;
        setFolder(payload);
        if (payload.is_subscribed) void markFolderRead(folderId).catch(() => undefined);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Cette chaîne est indisponible.");
      });
    return () => {
      active = false;
    };
  }, [folderId]);

  const toggleSubscription = async () => {
    if (!folder || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await toggleFolderSubscription(folder.id);
      setFolder({ ...folder, is_subscribed: result.is_subscribed });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "L’abonnement n’a pas pu être modifié.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublication = async () => {
    if (!folder || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await toggleFolderPublication(folder.id);
      setFolder({ ...folder, is_published: result.is_published });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La publication n’a pas pu être modifiée.");
    } finally {
      setSaving(false);
    }
  };

  const toggleOfficial = async () => {
    if (!folder || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await toggleFolderOfficial(folder.id);
      setFolder({ ...folder, is_official: result.is_official, is_published: result.is_published });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Le statut officiel n’a pas pu être modifié.");
    } finally {
      setSaving(false);
    }
  };

  if (error && folder === null) {
    return (
      <section className="feature-page">
        <button className="back-button" type="button" onClick={() => navigate("/mes-chaines")}>
          ← Retour aux chaînes
        </button>
        <div className="info-notice">{error}</div>
      </section>
    );
  }
  if (!folder) return <div className="collection-loading">Chargement de la chaîne…</div>;

  return (
    <section className="collection-page">
      <button className="back-button" type="button" onClick={() => navigate("/mes-chaines")}>
        ← Retour aux chaînes
      </button>
      <div className="collection-heading">
        <div>
          <p className="eyebrow">{folder.subject.name}</p>
          <h1>{folder.title}</h1>
          <p>{folder.description ?? "Cette chaîne ne possède pas encore de description."}</p>
        </div>
        <div className="collection-heading-actions">
          <button className="button button-primary" type="button" disabled={saving} onClick={() => void toggleSubscription()}>
            {saving ? "Enregistrement…" : folder.is_subscribed ? "Se désabonner" : "S’abonner"}
          </button>
          {folder.can_publish && (
            <button className="button" type="button" disabled={saving} onClick={() => void togglePublication()}>
              {folder.is_published ? "Repasser en brouillon" : "Publier la chaîne"}
            </button>
          )}
          {folder.can_edit && (
            <button className="button" type="button" onClick={() => navigate(`/mes-chaines/${folder.id}/modifier`)}>
              Modifier la chaîne
            </button>
          )}
          {folder.can_validate_official && (
            <button className="button" type="button" disabled={saving} onClick={() => void toggleOfficial()}>
              {folder.is_official ? "Retirer le statut officiel" : "Valider comme ressource officielle"}
            </button>
          )}
        </div>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {folder.collections.length ? (
        <div className="collection-grid">
          {folder.collections.map((collection) => (
            <article className="collection-tile" key={collection.id}>
              <div className="collection-tile-top"><span>{collection.subject.name}</span></div>
              <h2>{collection.title}</h2>
              <p>{collection.description ?? "Aucune description."}</p>
              <div className="collection-meta">
                <span>{collection.cards_count} carte{collection.cards_count > 1 ? "s" : ""}</span>
              </div>
              <button className="button button-primary" type="button" onClick={() => navigate(collection.path)}>
                Ouvrir la série
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="empty-state collection-empty">Cette chaîne ne contient encore aucune série visible.</div>
      )}
    </section>
  );
}

function FavoritesPage() {
  const [favorites, setFavorites] = useState<FavoritesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collectionPage, setCollectionPage] = useState(1);
  const [folderPage, setFolderPage] = useState(1);

  useEffect(() => {
    let active = true;
    void fetchFavorites(collectionPage, folderPage)
      .then(setFavorites)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Les favoris sont indisponibles."));
    const unsubscribe = subscribeFavoriteChanges(() => {
      void fetchFavorites(collectionPage, folderPage).then((payload) => {
        if (active) setFavorites(payload);
      }).catch(() => undefined);
    });
    return () => { active = false; unsubscribe(); };
  }, [collectionPage, folderPage]);

  const removeCollection = (collectionId: number) => {
    setFavorites((current) => current ? { ...current, collections: current.collections.filter((item) => item.id !== collectionId) } : null);
  };

  return (
    <section className="collection-page">
      <button className="back-button" type="button" onClick={() => navigate("/")}>← Retour au tableau de bord</button>
      <div className="collection-heading">
        <div><p className="eyebrow">Bibliothèque personnelle</p><h1>Mes favoris</h1><p>Retrouvez vos séries favorites et les chaînes que vous suivez.</p></div>
      </div>
      {error && <div className="info-notice">{error}</div>}
      {!favorites && !error ? <div className="collection-loading">Chargement des favoris…</div> : favorites && (
        <>
          <h2>Séries favorites</h2>
          {favorites.collections.length ? <div className="collection-grid">{favorites.collections.map((collection) => (
            <article className="collection-tile" key={collection.id}>
              <div className="collection-tile-top"><span>{collection.subject.name}</span></div>
              <h3>{collection.title}</h3><p>{collection.description ?? "Aucune description."}</p>
              <FavoriteButton collectionId={collection.id} isFavorite onChanged={(favorite) => { if (!favorite) removeCollection(collection.id); }} />
              <button className="button button-primary" type="button" onClick={() => navigate(collection.path)}>Ouvrir la série</button>
            </article>
          ))}</div> : <div className="empty-state">Aucune série favorite pour le moment.</div>}
          {favorites.pagination.collections.pages > 1 && <div className="history-pagination"><button className="button" type="button" disabled={!favorites.pagination.collections.has_previous} onClick={() => setCollectionPage((value) => value - 1)}>← Précédente</button><span>Page {favorites.pagination.collections.page} sur {favorites.pagination.collections.pages}</span><button className="button" type="button" disabled={!favorites.pagination.collections.has_next} onClick={() => setCollectionPage((value) => value + 1)}>Suivante →</button></div>}
          <h2>Chaînes suivies</h2>
          {favorites.folders.length ? <div className="collection-grid">{favorites.folders.map((folder) => (
            <article className="collection-tile" key={folder.id}>
              <div className="collection-tile-top"><span>{folder.subject.name}</span></div>
              <h3>{folder.title}</h3><p>{folder.description ?? "Aucune description."}</p>
              <button className="button button-primary" type="button" onClick={() => navigate(folder.path)}>Ouvrir la chaîne</button>
            </article>
          ))}</div> : <div className="empty-state">Aucune chaîne suivie pour le moment.</div>}
          {favorites.pagination.folders.pages > 1 && <div className="history-pagination"><button className="button" type="button" disabled={!favorites.pagination.folders.has_previous} onClick={() => setFolderPage((value) => value - 1)}>← Précédente</button><span>Page {favorites.pagination.folders.page} sur {favorites.pagination.folders.pages}</span><button className="button" type="button" disabled={!favorites.pagination.folders.has_next} onClick={() => setFolderPage((value) => value + 1)}>Suivante →</button></div>}
        </>
      )}
    </section>
  );
}

const difficultyLabels: Record<MemoDifficulty, string> = {
  SOFT_DIFFICULTY: "Facile · 3 cartes",
  NORMAL_DIFFICULTY: "Normale · 6 cartes",
  HARD_DIFFICULTY: "Difficile · 9 cartes",
};

function MemoGame({
  collectionId,
  duelId,
  initialSessionToken,
  onScoreUpdated,
}: {
  collectionId: number;
  duelId?: number;
  initialSessionToken?: string;
  onScoreUpdated: (score: UserScoreSnapshot) => void;
}) {
  const [difficulty, setDifficulty] = useState<MemoDifficulty>("SOFT_DIFFICULTY");
  const [session, setSession] = useState<MemoSession | null>(null);
  const [phase, setPhase] = useState<"setup" | "memorize" | "play" | "finished" | "abandoned">("setup");
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [successfulCardIds, setSuccessfulCardIds] = useState<number[]>([]);
  const [errors, setErrors] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [experienceFeedback, setExperienceFeedback] = useState<ExperienceFeedback | null>(null);
  const [progress, setProgress] = useState<ExperienceProgress | undefined>();
  const [reward, setReward] = useState<ExperienceReward | null>(null);
  const [result, setResult] = useState<MemoResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef(0);
  const initialSessionLoaded = useRef(false);

  useEffect(() => {
    if (phase !== "memorize") return;
    if (remainingSeconds <= 0) {
      setPhase("play");
      startedAt.current = Date.now();
      return;
    }
    const timer = window.setTimeout(() => setRemainingSeconds((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [phase, remainingSeconds]);

  const begin = async (resumeToken?: string) => {
    setLoading(true);
    setError(null);
    try {
      const nextSession = resumeToken
        ? await fetchMemoSession(resumeToken)
        : duelId
        ? (await startDuelSession(duelId) as MemoSession)
        : await startMemo(collectionId, difficulty);
      setSession(nextSession);
      setRemainingSeconds(nextSession.memorization_seconds);
      setSuccessfulCardIds(nextSession.successful_card_ids ?? []);
      setErrors(nextSession.stats?.errors ?? 0);
      setResult(nextSession.result ?? null);
      setProgress(nextSession.progress);
      setExperienceFeedback(nextSession.feedback ?? null);
      setReward(null);
      const alreadyStarted = Boolean(
        nextSession.stats?.answers || nextSession.stats?.errors || nextSession.successful_card_ids?.length,
      );
      setPhase(nextSession.status === "abandoned" ? "abandoned" : nextSession.finished ? "finished" : alreadyStarted ? "play" : "memorize");
      if (alreadyStarted) startedAt.current = Date.now();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible de démarrer Memo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialSessionToken || initialSessionLoaded.current) return;
    initialSessionLoaded.current = true;
    void begin(initialSessionToken);
  }, [initialSessionToken]);

  const activeFront = session?.fronts.find(
    (item) => !successfulCardIds.includes(item.card_id),
  );

  const chooseBack = async (backCardId: number) => {
    if (!session || !activeFront || loading) return;
    setLoading(true);
    setFeedback(null);
    try {
      const attempt = await submitMemoAttempt(
        session.token,
        activeFront.card_id,
        backCardId,
        Math.floor((Date.now() - startedAt.current) / 1000),
      );
      setSuccessfulCardIds(attempt.successful_card_ids);
      setErrors(attempt.stats.errors);
      setFeedback(attempt.correct ? "Bonne association !" : "Cette carte ne correspond pas.");
      setExperienceFeedback(attempt.feedback ?? null);
      setProgress(attempt.progress);
      setReward(attempt.reward ?? null);
      if (attempt.finished) {
        setResult(attempt.result);
        setPhase("finished");
        if (attempt.user_score) onScoreUpdated(attempt.user_score);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "La réponse n’a pas été enregistrée.");
    } finally {
      setLoading(false);
    }
  };

  const abandon = async () => {
    if (!session) return;
    setError(null);
    try {
      const response = await abandonGameSession(session.token);
      setExperienceFeedback(response.feedback);
      setPhase("abandoned");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Impossible d’interrompre l’activité.");
    }
  };

  return (
    <section className="memo-page">
      <button className="back-button" type="button" onClick={() => navigate(duelId ? "/mes-duels" : `/series/${collectionId}`)}>
        ← Retour {duelId ? "aux duels" : "à la série"}
      </button>
      {phase === "setup" && (
        <div className="memo-panel memo-setup">
          <ActivityGuide guide={{ ...defaultGuide, target_count: difficulty === "SOFT_DIFFICULTY" ? 3 : difficulty === "NORMAL_DIFFICULTY" ? 6 : 9 }} activeStep={0} />
          <p className="eyebrow">Jeu pédagogique</p>
          <h1>Memo</h1>
          <p className="lead">Mémorisez les paires, puis associez chaque recto à son verso.</p>
          {duelId ? (
            <div className="info-notice"><strong>Paramètres du duel verrouillés</strong><p>La série, la difficulté et l’ordre des cartes sont identiques pour les deux joueurs.</p></div>
          ) : <fieldset>
            <legend>Difficulté</legend>
            {Object.entries(difficultyLabels).map(([value, label]) => (
              <label key={value}>
                <input
                  type="radio"
                  name="memo-difficulty"
                  value={value}
                  checked={difficulty === value}
                  onChange={() => setDifficulty(value as MemoDifficulty)}
                />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>}
          {error && <p className="game-error">{error}</p>}
          <button className="button button-primary" type="button" disabled={loading} onClick={() => void begin()}>
            {loading ? "Préparation…" : "Commencer"}
          </button>
        </div>
      )}

      {phase === "memorize" && session && (
        <div className="memo-panel">
          <ActivityGuide guide={session.guide} activeStep={1} />
          <div className="memo-status">
            <div><span>Temps de mémorisation</span><strong>{remainingSeconds}s</strong></div>
            <button type="button" onClick={() => { setPhase("play"); startedAt.current = Date.now(); }}>
              J’ai mémorisé
            </button>
          </div>
          <div className="memo-preview-grid">
            {session.fronts.map((front) => {
              const back = session.backs.find((item) => item.card_id === front.card_id);
              return (
                <article key={front.card_id}>
                  <div><span>Recto</span><FaceContent face={front.face} /></div>
                  <div><span>Verso</span><FaceContent face={back?.face} /></div>
                </article>
              );
            })}
          </div>
          {!duelId && <AbandonActivityButton disabled={loading} onConfirm={abandon} />}
        </div>
      )}

      {phase === "play" && session && activeFront && (
        <div className="memo-panel">
          <ActivityGuide guide={session.guide} activeStep={experienceFeedback ? 3 : 2} />
          <SessionProgressBar progress={progress} />
          <div className="memo-status">
            <div><span>Paires trouvées</span><strong>{successfulCardIds.length}/{session.target_count}</strong></div>
            <div><span>Erreurs</span><strong>{errors}/3</strong></div>
          </div>
          <div className="memo-question">
            <p className="card-side-label">Retrouvez le verso de ce recto</p>
            <FaceContent face={activeFront.face} />
          </div>
          <div className="memo-answers" aria-label="Versos proposés">
            {session.backs.map((back) => (
              <button
                key={back.card_id}
                data-card-id={back.card_id}
                type="button"
                disabled={loading || successfulCardIds.includes(back.card_id)}
                onClick={() => void chooseBack(back.card_id)}
              >
                <FaceContent face={back.face} />
              </button>
            ))}
          </div>
          {!experienceFeedback && <p className={feedback?.startsWith("Bonne") ? "game-feedback success" : "game-feedback"} aria-live="polite">
            {feedback}
          </p>}
          <FeedbackNotice feedback={experienceFeedback} />
          {error && <p className="game-error">{error}</p>}
          {!duelId && <AbandonActivityButton disabled={loading} onConfirm={abandon} />}
        </div>
      )}

      {phase === "abandoned" && experienceFeedback && (
        <AbandonedActivityPanel feedback={experienceFeedback} onReturn={() => navigate(`/series/${collectionId}`)} />
      )}

      {phase === "finished" && result && (
        <div className={`memo-panel memo-result ${result.won ? "won" : "lost"}`}>
          <ActivityGuide guide={session?.guide} activeStep={4} />
          <p className="eyebrow">Partie terminée</p>
          <h1>{result.won ? "Bravo !" : "Encore un essai"}</h1>
          <strong className="memo-score">{result.score} point{result.score > 1 ? "s" : ""}</strong>
          <p>{result.won ? "Toutes les paires ont été retrouvées." : "Trois associations incorrectes ont terminé la partie."}</p>
          <RewardSummary reward={reward} />
          <div className="memo-result-actions">
            {!duelId && <button className="button button-primary" type="button" onClick={() => setPhase("setup")}>Rejouer</button>}
            <button className="button" type="button" onClick={() => navigate(duelId ? "/mes-duels" : `/series/${collectionId}`)}>
              {duelId ? "Voir le duel" : "Retour à la série"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function DashboardHome({ dashboard, session }: Omit<AuthenticatedProps, "onScoreUpdated" | "onProfileUpdated">) {
  const user = session.user;
  const personaLabel = dashboard.persona === "teacher" ? "Espace enseignant" : "Espace élève";

  return (
    <>
      <section className="dashboard-hero">
        <div>
          <p className="eyebrow">{personaLabel}</p>
          <h1>Bonjour, {user.display_name}</h1>
          <p>
            Retrouvez vos séries, vos jeux et vos activités pédagogiques dans un espace unique.
          </p>
          {session.user.roles.includes("ROLE_STUDENT") && <QuickPlayButton />}
        </div>
        <div className="score-card" aria-label={`Score actuel : ${user.score} points`}>
          <span>Score actuel</span>
          <strong>{user.score.toLocaleString("fr-FR")}</strong>
          <small>{user.all_time_score.toLocaleString("fr-FR")} points cumulés</small>
        </div>
      </section>

      {session.user.roles.includes("ROLE_STUDENT") && <DailyGoalCard />}

      <section className="dashboard-section" aria-labelledby="pathways-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Parcours</p>
            <h2 id="pathways-title">Que souhaitez-vous faire ?</h2>
          </div>
          <span className="section-chip">Vos activités</span>
        </div>
        <div className="destination-grid">
          {dashboard.destinations.map((destination) => (
            <DestinationCard key={destination.identifier} destination={destination} />
          ))}
        </div>
      </section>

      <div className="dashboard-columns">
        <section className="dashboard-section subjects-section" aria-labelledby="subjects-title">
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">Disciplines</p>
              <h2 id="subjects-title">Matières</h2>
            </div>
          </div>
          {dashboard.subjects.length > 0 ? (
            <ul className="subject-list">
              {dashboard.subjects.map((subject) => (
                <li key={subject.id}>
                  <span
                    className="subject-color"
                    style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(subject.color) ? subject.color : "#5f63bb" }}
                    aria-hidden="true"
                  />
                  {subject.name}
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-state">
              <strong>Aucune matière enregistrée</strong>
              <p>Ajoutez les disciplines scolaires dans l’administration Cogito.</p>
              {user.is_staff && <a href="/admin/schools/schoolsubject/">Gérer les matières</a>}
            </div>
          )}
        </section>

        <section className="dashboard-section news-section" aria-labelledby="news-title">
          <div className="section-heading compact">
            <div>
              <p className="eyebrow">Informations</p>
              <h2 id="news-title">Actualités</h2>
            </div>
          </div>
          {dashboard.news.length > 0 ? (
            <div className="news-list">
              {dashboard.news.map((item) => (
                <article key={item.id}>
                  <time dateTime={item.publication_start_date}>
                    {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(
                      new Date(item.publication_start_date),
                    )}
                  </time>
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <strong>Vous êtes à jour</strong>
              <p>Aucune actualité publiée pour votre profil.</p>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

interface AuthenticatedProps {
  session: Extract<SessionResponse, { authenticated: true }>;
  dashboard: DashboardResponse;
  onScoreUpdated: (score: UserScoreSnapshot) => void;
  onProfileUpdated: (user: Pick<SessionUser, "username" | "display_name" | "avatar_url">) => void;
}

function AuthenticatedApp({ session, dashboard, onScoreUpdated, onProfileUpdated }: AuthenticatedProps) {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const destination = destinationForPath(dashboard.destinations, path);
  const duelParameter = new URLSearchParams(window.location.search).get("duel");
  const initialSessionToken = new URLSearchParams(window.location.search).get("session") ?? undefined;
  const parsedDuelId = duelParameter === null ? undefined : Number(duelParameter);
  const duelId = parsedDuelId && Number.isInteger(parsedDuelId) ? parsedDuelId : undefined;
  const memoMatch = /^\/series\/(\d+)\/memo\/?$/.exec(path);
  const revisioMatch = /^\/series\/(\d+)\/revisio\/?$/.exec(path);
  const practiceMatch = /^\/series\/(\d+)\/(association|brio|typographie|quizako)\/?$/.exec(path);
  const seriesMatch = /^\/series\/(\d+)\/?$/.exec(path);
  const folderMatch = /^\/chaines\/(\d+)\/?$/.exec(path);
  const folderEditMatch = /^\/mes-chaines\/(\d+)\/modifier\/?$/.exec(path);
  const practiceSlugs: Record<string, PracticeGameSlug> = {
    association: "asso",
    brio: "brio",
    typographie: "typo",
    quizako: "quizako",
  };
  const canImport = session.user.roles.some((role) => ["ROLE_TEACHER", "ROLE_HEADTEACHER", "ROLE_ADMIN"].includes(role));
  const canOfficial = session.user.roles.some((role) => ["ROLE_HEADTEACHER", "ROLE_ADMIN"].includes(role));

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Aller au contenu
      </a>
      <header className="app-header">
        <Brand />
        <div className="user-actions">
          {session.user.roles.includes("ROLE_STUDENT") && <QuickPlayButton compact />}
          <a className="user-identity" href="/mon-profil" onClick={(event) => { event.preventDefault(); navigate("/mon-profil"); }}>
            <span className="header-avatar">
              {session.user.avatar_url ? <img src={session.user.avatar_url} alt="" /> : session.user.display_name.slice(0, 1).toUpperCase()}
            </span>
            <span className="user-identity-copy"><strong>{session.user.display_name}</strong><small>{session.user.primary_role_label}</small></span>
          </a>
          {session.user.is_staff && <a href="/admin/">Administration</a>}
          <form action={session.auth.logout_url} method="post">
            <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken()} />
            <button type="submit">Se déconnecter</button>
          </form>
        </div>
      </header>
      <main id="main-content" className="app-main">
        {path === "/" ? (
          <DashboardHome session={session} dashboard={dashboard} />
        ) : path === "/mon-profil" ? (
          <ProfilePage onIdentityUpdated={onProfileUpdated} />
        ) : path === "/mes-series" ? (
          <CollectionLibrary official={false} canImport={canImport} />
        ) : path === "/mes-series/nouvelle" ? (
          <CollectionEditor subjects={dashboard.subjects} canOfficial={canOfficial} />
        ) : path === "/mes-series/importer" ? (
          <CatalogImportPage />
        ) : path === "/mes-chaines" ? (
          <FolderLibrary canOfficial={canOfficial} />
        ) : path === "/mes-chaines/nouvelle" ? (
          <FolderEditor subjects={dashboard.subjects} canOfficial={canOfficial} />
        ) : folderEditMatch ? (
          <FolderEditor subjects={dashboard.subjects} folderId={Number(folderEditMatch[1])} canOfficial={canOfficial} />
        ) : path === "/mes-favoris" ? (
          <FavoritesPage />
        ) : path === "/ressources-officielles" ? (
          <CollectionLibrary official canImport={false} />
        ) : path === "/mes-revisions" ? (
          <ReviewLibrary />
        ) : path === "/mes-duels" ? (
          <DuelsPage />
        ) : path === "/historique" ? (
          <HistoryPage />
        ) : path === "/mes-recommandations" ? (
          <RecommendationsPage />
        ) : path === "/mes-classes" ? (
          <ClassroomsPage persona={dashboard.persona} />
        ) : memoMatch ? (
          <MemoGame collectionId={Number(memoMatch[1])} duelId={duelId} initialSessionToken={initialSessionToken} onScoreUpdated={onScoreUpdated} />
        ) : revisioMatch ? (
          <RevisioGame collectionId={Number(revisioMatch[1])} initialSessionToken={initialSessionToken} onScoreUpdated={onScoreUpdated} />
        ) : practiceMatch ? (
          <OtherGame game={practiceSlugs[practiceMatch[2]!]!} collectionId={Number(practiceMatch[1])} duelId={duelId} initialSessionToken={initialSessionToken} onScoreUpdated={onScoreUpdated} />
        ) : seriesMatch ? (
          <CollectionStudy collectionId={Number(seriesMatch[1])} />
        ) : folderMatch ? (
          <FolderStudy folderId={Number(folderMatch[1])} />
        ) : destination ? (
          <FeatureUnavailable destination={destination} />
        ) : (
          <section className="not-found">
            <p className="eyebrow">Page inconnue</p>
            <h1>Ce parcours n’est pas encore référencé.</h1>
            <button className="button button-primary" type="button" onClick={() => navigate("/")}>
              Revenir à l’accueil
            </button>
          </section>
        )}
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const applyScoreSnapshot = useCallback((score: UserScoreSnapshot) => {
    setSession((current) => {
      if (!current?.authenticated) return current;
      return {
        ...current,
        user: { ...current.user, score: score.score, all_time_score: score.all_time_score },
      };
    });
  }, []);

  const publishScoreSnapshot = useCallback((score: UserScoreSnapshot) => {
    applyScoreSnapshot(score);
    publishScoreChange(score);
  }, [applyScoreSnapshot]);

  const applyProfileUpdate = useCallback((update: Pick<SessionUser, "username" | "display_name" | "avatar_url">) => {
    setSession((current) => current?.authenticated
      ? { ...current, user: { ...current.user, ...update } }
      : current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextSession = await fetchSession();
      setSession(nextSession);
      setDashboard(nextSession.authenticated ? await fetchDashboard() : null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Le service Cogito est indisponible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return subscribeScoreBroadcasts(applyScoreSnapshot);
  }, [applyScoreSnapshot]);

  useEffect(() => {
    const refreshSession = () => {
      if (document.visibilityState === "hidden") return;
      void fetchSession().then(setSession).catch(() => undefined);
    };
    window.addEventListener("focus", refreshSession);
    document.addEventListener("visibilitychange", refreshSession);
    return () => {
      window.removeEventListener("focus", refreshSession);
      document.removeEventListener("visibilitychange", refreshSession);
    };
  }, []);

  if (loading) return <LoadingScreen />;
  if (error || session === null) {
    return (
      <main className="centered-state error-state">
        <Brand />
        <p className="eyebrow">Connexion au service impossible</p>
        <h1>Le tableau de bord ne peut pas être chargé.</h1>
        <p>{error}</p>
        <button className="button button-primary" type="button" onClick={() => void load()}>
          Réessayer
        </button>
      </main>
    );
  }
  if (!session.authenticated) return <AnonymousLanding session={session} />;
  if (dashboard === null) return <LoadingScreen />;
  return <AuthenticatedApp session={session} dashboard={dashboard} onScoreUpdated={publishScoreSnapshot} onProfileUpdated={applyProfileUpdate} />;
}
